const normalizeSchemaInput = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const schemaEntries = (schema = {}) => Object.entries(normalizeSchemaInput(schema));

const schemaTypeTokens = (type) => String(type || '')
  .trim()
  .toLowerCase()
  .split(/[^a-z0-9_]+/)
  .filter(Boolean);

const mergeSchemaType = (currentType, nextType) => {
  const currentTokens = schemaTypeTokens(currentType);
  const nextTokens = schemaTypeTokens(nextType);
  if (!currentTokens.length) return nextTokens.join('|');
  if (!nextTokens.length) return currentTokens.join('|');
  return [...new Set([...currentTokens, ...nextTokens])]
    .sort((left, right) => left.localeCompare(right, 'pt-BR'))
    .join('|');
};

const normalizeSchemaPath = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const noRoot = raw.replace(/^\$\./, '').replace(/^\$/, '');
  if (!noRoot || noRoot === '[]' || noRoot === '[0]' || noRoot === '0') return '$';
  return noRoot
    .replace(/\[\]/g, '.0')
    .replace(/\[(\d+)\]/g, '.$1')
    .replace(/^\./, '')
    .replace(/\.\./g, '.');
};

const inferSchemaType = (value) => {
  if (Array.isArray(value)) return 'array';
  if (value == null) return 'null';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'object') return 'object';
  return 'string';
};

const inferSchemaFromSample = (sample, options = {}) => {
  const maxDepth = Number.isInteger(options.maxDepth) ? options.maxDepth : 5;
  const maxEntries = Number.isInteger(options.maxEntries) ? options.maxEntries : 120;
  const maxArrayItems = Number.isInteger(options.maxArrayItems) ? options.maxArrayItems : 25;
  const schema = {};

  const registerPath = (path, type) => {
    const normalizedPath = normalizeSchemaPath(path);
    if (!normalizedPath) return;
    schema[normalizedPath] = mergeSchemaType(schema[normalizedPath], type);
  };

  const visit = (value, path = '', depth = 0) => {
    if (Object.keys(schema).length >= maxEntries) return;
    const type = inferSchemaType(value);

    if (path) {
      registerPath(path, type);
    } else if (Array.isArray(value)) {
      registerPath('$', type);
    }

    if (depth >= maxDepth) return;

    if (Array.isArray(value)) {
      if (!value.length) return;
      const sampleItems = value.slice(0, Math.max(1, Math.min(maxArrayItems, value.length)));
      sampleItems.forEach((item) => {
        visit(item, path ? `${path}.0` : '0', depth + 1);
      });
      return;
    }

    if (!value || typeof value !== 'object') return;

    Object.entries(value).forEach(([key, child]) => {
      const childPath = path ? `${path}.${key}` : key;
      visit(child, childPath, depth + 1);
    });
  };

  visit(sample, '', 0);
  return schema;
};

const parseJsonLikeValue = (value) => {
  if (value == null) return null;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const runtimeOutputToSample = (output) => {
  if (!output || typeof output !== 'object' || Array.isArray(output)) {
    return parseJsonLikeValue(output);
  }

  if (Object.prototype.hasOwnProperty.call(output, 'body_json')) {
    const bodyJson = output.body_json;
    if (bodyJson !== undefined && bodyJson !== null) return bodyJson;
    if (Object.prototype.hasOwnProperty.call(output, 'body')) {
      return parseJsonLikeValue(output.body);
    }
    return bodyJson;
  }

  if (Object.prototype.hasOwnProperty.call(output, 'body')) {
    return parseJsonLikeValue(output.body);
  }

  return output;
};

const mergeResponseSchemas = (...schemas) => {
  const merged = {};
  schemas.forEach((schema) => {
    schemaEntries(schema).forEach(([key, definition]) => {
      const normalizedPath = normalizeSchemaPath(key) || String(key || '').trim();
      if (!normalizedPath) return;
      const inferredType = typeof definition === 'string' ? definition : definition?.type;
      const type = String(inferredType || '').trim();
      if (!type) return;
      merged[normalizedPath] = mergeSchemaType(merged[normalizedPath], type);
    });
  });
  return merged;
};

const schemaFromRuntimeOutput = (output, options = {}) => {
  const sample = runtimeOutputToSample(output);
  const inferred = (sample == null || sample === '')
    ? {}
    : inferSchemaFromSample(sample, options);
  const declared = options.includeDeclaredSchema === false
    ? {}
    : normalizeSchemaInput(output?.response_schema);
  return mergeResponseSchemas(declared, inferred);
};

const schemaFromInspectionInput = (input, options = {}) => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  return inferSchemaFromSample(input, {
    maxEntries: 240,
    ...options
  });
};

const schemaFromTaskPayload = (task = {}, options = {}) => {
  const payload = task && typeof task === 'object' && !Array.isArray(task)
    ? task.payload
    : null;
  if (payload == null) return {};
  return inferSchemaFromSample(payload, options);
};

const schemasEqual = (left = {}, right = {}) => {
  const normalizedLeft = mergeResponseSchemas(left);
  const normalizedRight = mergeResponseSchemas(right);
  const leftKeys = Object.keys(normalizedLeft).sort();
  const rightKeys = Object.keys(normalizedRight).sort();
  if (leftKeys.length !== rightKeys.length) return false;
  for (let index = 0; index < leftKeys.length; index += 1) {
    const key = leftKeys[index];
    if (key !== rightKeys[index]) return false;
    if (normalizedLeft[key] !== normalizedRight[key]) return false;
  }
  return true;
};

module.exports = {
  mergeResponseSchemas,
  schemaFromInspectionInput,
  schemaFromRuntimeOutput,
  schemaFromTaskPayload,
  schemasEqual
};
